import React, { useContext, useEffect, useState } from 'react';
import { client, consoleLogInDev } from 'shared';
import { Label } from 'models';

export type AllLabelsKeys =
    | 'business'
    | 'ongoing'
    | 'applicants'
    | 'interview'
    | 'admin-providers'
    | 'external-message'
    | 'external-message-thread';

export type AllLabelsType = {
    [key in AllLabelsKeys]?: Label[];
};

interface AdminAppContextType {
    providerLabels: Label[];
    businessLabels: Label[];
    jobLabels: Label[];
    applicantLabels: Label[];
    externalMessageLabels: Label[];
    externalMessageThreadLabels: Label[];
    allLabels: AllLabelsType;
    refreshLabels: () => void;
    getLabelByName: (type: AllLabelsKeys, name: string) => Label | undefined;
}

export const AdminAppContext = React.createContext<AdminAppContextType>({
    providerLabels: [],
    businessLabels: [],
    jobLabels: [],
    applicantLabels: [],
    externalMessageLabels: [],
    externalMessageThreadLabels: [],
    allLabels: {},
    refreshLabels: () => {},
    getLabelByName: () => undefined,
});

export function AdminAppContextProvider({ children }: { children: React.ReactNode }) {
    const [businessLabels, setBusinessLabels] = useState<Label[]>([]);
    const [jobLabels, setJobLabels] = useState<Label[]>([]);
    const [applicantLabels, setApplicantLabels] = useState<Label[]>([]);
    const [interviewLabels, setInterviewLabels] = useState<Label[]>([]);
    const [providerLabels, setProviderLabels] = useState<Label[]>([]);
    const [externalMessageLabels, setExternalMessageLabels] = useState<Label[]>([]);
    const [externalMessageThreadLabels, setExternalMessageThreadLabels] = useState<Label[]>([]);
    const [allLabels, setAllLabels] = useState<AllLabelsType>({});

    const getLabels = () => {
        client('api/label/')
            .then((results) => {
                setBusinessLabels(results.filter((label: Label) => label.content_type === 'business'));
                setJobLabels(results.filter((label: Label) => label.content_type === 'ongoingrequest'));
                setApplicantLabels(results.filter((label: Label) => label.content_type === 'applicant'));
                setInterviewLabels(results.filter((label: Label) => label.content_type === 'interview'));
                setProviderLabels(results.filter((label: Label) => label.content_type === 'babysitter'));
                setExternalMessageLabels(results.filter((label: Label) => label.content_type === 'externalmessage'));
                setExternalMessageThreadLabels(
                    results.filter((label: Label) => label.content_type === 'externalmessagethread'),
                );
            })
            .catch(consoleLogInDev);
    };

    const getLabelByName = (type: AllLabelsKeys, name: string) => {
        return allLabels[type]?.find((label) => label.text === name);
    };

    useEffect(getLabels, []);
    useEffect(() => {
        setAllLabels({
            business: businessLabels,
            ongoing: jobLabels,
            applicants: applicantLabels,
            interview: interviewLabels,
            'admin-providers': providerLabels,
            'external-message': externalMessageLabels,
            'external-message-thread': externalMessageThreadLabels,
        });
    }, [
        businessLabels,
        jobLabels,
        applicantLabels,
        interviewLabels,
        providerLabels,
        externalMessageLabels,
        externalMessageThreadLabels,
    ]);

    const contextValue = {
        providerLabels,
        businessLabels,
        jobLabels,
        applicantLabels,
        externalMessageLabels,
        externalMessageThreadLabels,
        allLabels,
        refreshLabels: getLabels,
        getLabelByName,
    };
    return <AdminAppContext.Provider value={contextValue}>{children}</AdminAppContext.Provider>;
}

export const useAdminAppContext = () => {
    return useContext(AdminAppContext);
};
